import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../networking/axiosconfig";
import { User, Camera, Upload, X } from "lucide-react";
import { toast } from "react-hot-toast";

const ProfileImageUpload = ({ userId, token, onImageUpdate }) => {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCurrentImage();
  }, [userId]);

  const fetchCurrentImage = async () => {
    if (!userId || !token) {
      setFetching(false);
      return;
    }

    try {
      setFetching(true);
      const response = await axiosInstance.get(`/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token.trim()}` }
      });

      if (response.data?.status && response.data?.data) {
        const profileImage = response.data.data.user_profile_img;
        if (profileImage) {
          // If the image is a full URL, use it directly, otherwise construct the URL
          const imageUrl = profileImage.startsWith('http') 
            ? profileImage 
            : `${axiosInstance.defaults.baseURL}${profileImage.startsWith('/') ? '' : '/'}${profileImage}`;
          setCurrentImage(imageUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching profile image:', err);
    } finally {
      setFetching(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!image) {
      toast.error("Please select an image");
      return;
    }

    const formData = new FormData();
    formData.append("image", image);

    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `/api/users/edit_profile_picture/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token.trim()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data?.status) {
        toast.success("Profile picture updated successfully!");
        // Update the current image with the preview
        setCurrentImage(preview);
        setImage(null);
        setPreview(null);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Notify parent component if callback provided
        if (onImageUpdate) {
          onImageUpdate(preview);
        }
        // Refresh the image from server after a short delay
        setTimeout(() => {
          fetchCurrentImage();
        }, 500);
      } else {
        toast.error(res.data?.message || "Failed to update profile picture");
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.message || "Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePreview = () => {
    setImage(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayImage = preview || currentImage;

  return (
    <div className="relative group">
      {/* Profile Image Container */}
      <div className="relative">
        <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-xl flex items-center justify-center ring-2 ring-white ring-opacity-50 relative">
          {fetching ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-indigo-500 absolute inset-0">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : displayImage ? (
            <img
              src={displayImage}
              alt="Profile"
              className="w-full h-full object-cover relative z-10"
              style={{ imageRendering: 'auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.parentElement.querySelector('.fallback-icon');
                if (fallback) {
                  fallback.style.display = 'flex';
                }
              }}
            />
          ) : null}
          {!displayImage && !fetching && (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-indigo-500 absolute inset-0">
              <User size={40} className="text-white" />
            </div>
          )}
        </div>

        {/* Upload Overlay */}
        <label
          htmlFor="profile-image-upload"
          className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-60 flex items-center justify-center cursor-pointer transition-all duration-200"
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1">
            <Camera size={20} className="text-white" />
            <span className="text-xs text-white font-medium">Change</span>
          </div>
        </label>

        {/* Hidden File Input */}
        <input
          id="profile-image-upload"
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
      </div>

      {/* Upload Controls - Show when image is selected */}
      {preview && (
        <div className="absolute top-28 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-2xl p-4 z-50 min-w-[240px] border border-gray-200 animate-in fade-in slide-in-from-top-2">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-gray-800">Image Preview</span>
              <button
                onClick={handleRemovePreview}
                className="text-gray-400 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded"
                type="button"
                title="Cancel"
              >
                <X size={18} />
              </button>
            </div>
            <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-2 border-gray-200 shadow-md">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            </div>
            <button
              onClick={handleUpload}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              type="button"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload size={16} />
                  <span>Upload Image</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImageUpload;
